import React, { useState, useEffect } from 'react';
import { Button } from '../common/Button';
import type { StatusChangeModalProps, ProductionStatus } from '../../types';
import {
  Factory,
  AlertTriangle,
  Settings,
  ArrowRight,
  FileText,
  type LucideIcon,
} from 'lucide-react';

const STATUS_CONFIG: Record<
  ProductionStatus,
  {
    label: string;
    icon: LucideIcon;
    color: string;
    bgColor: string;
    textColor: string;
  }
> = {
  new_orders: {
    label: 'New Orders',
    icon: Factory,
    color: 'blue',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
  },
  problem_orders: {
    label: 'Problem Orders', 
    icon: AlertTriangle,
    color: 'red',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
  },
  in_production: {
    label: 'In Production',
    icon: Settings,
    color: 'green', 
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
  },
};

export const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentStatus,
  targetStatus,
  jobInfo,
  isLoading = false
}) => {
  const [productionNotes, setProductionNotes] = useState<string>('');
  const [useActualManufactureDateInEmail, setUseActualManufactureDateInEmail] = useState(false);

  // Initialize when modal opens
  useEffect(() => {
    if (isOpen) {
      setProductionNotes(jobInfo.production_notes || '');
      setUseActualManufactureDateInEmail(false);
    }
  }, [isOpen, jobInfo.production_notes, targetStatus]);

  if (!isOpen || !targetStatus) return null;

  const currentConfig = STATUS_CONFIG[currentStatus as ProductionStatus];
  const targetConfig = STATUS_CONFIG[targetStatus as ProductionStatus];
  const CurrentIcon = currentConfig.icon;
  const TargetIcon = targetConfig.icon;

  const handleConfirm = () => {
    const notesToSave = productionNotes.trim() || undefined;
    onConfirm({
      notes: notesToSave,
      useActualManufactureDateInEmail:
        targetStatus === 'in_production' ? useActualManufactureDateInEmail : undefined,
    });
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4 sm:p-6">
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        onClick={handleClose}
        aria-hidden
      />

      <div
        className="relative z-10 flex min-h-0 w-full max-w-lg max-h-[calc(min(100dvh,100vh)-2rem)] flex-col overflow-hidden rounded-lg bg-white text-left shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="status-change-modal-title"
      >
        {/* Header + status flow */}
        <div className="flex-shrink-0 px-4 pt-5 sm:px-6 sm:pt-6">
          <div className="flex items-center">
            <div className="mx-auto flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 sm:mx-0">
              <ArrowRight className="h-5 w-5 text-orange-600" />
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3
                id="status-change-modal-title"
                className="text-base font-semibold leading-6 text-gray-900"
              >
                Change Production Status
              </h3>
              <div className="mt-1.5">
                <p className="text-xs text-gray-500 sm:text-sm">
                  Move {jobInfo.customer_name}&apos;s job{' '}
                  {jobInfo.website_job_number ? `(#${jobInfo.website_job_number})` : ''}
                  from <strong>{currentConfig.label}</strong> to <strong>{targetConfig.label}</strong>
                </p>
              </div>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-center space-x-3">
            <div className="flex items-center space-x-2">
              <CurrentIcon className={`h-5 w-5 ${currentConfig.textColor}`} />
              <span
                className={`rounded-full px-2 py-1 text-xs font-medium ${currentConfig.bgColor} ${currentConfig.textColor}`}
              >
                {currentConfig.label}
              </span>
            </div>
            <ArrowRight className="h-4 w-4 text-gray-400" />
            <div className="flex items-center space-x-2">
              <TargetIcon className={`h-5 w-5 ${targetConfig.textColor}`} />
              <span
                className={`rounded-full px-2 py-1 text-xs font-medium ${targetConfig.bgColor} ${targetConfig.textColor}`}
              >
                {targetConfig.label}
              </span>
            </div>
          </div>
        </div>

        {/* Production Notes — scrolls if the viewport is short */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 sm:px-6">
          <div className="py-3">
            <div className="mb-2 flex items-center">
              <FileText className="mr-2 h-4 w-4 text-gray-600" />
              <label className="text-sm font-medium text-gray-700">Production Notes</label>
            </div>
            <textarea
              value={productionNotes}
              onChange={(e) => setProductionNotes(e.target.value)}
              placeholder="Add or update production notes (optional)..."
              rows={2}
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-orange-500"
              disabled={isLoading}
            />
            {productionNotes.trim() && (
              <p className="mt-1 text-xs text-gray-500">
                Notes will be updated when status is changed
              </p>
            )}
          </div>
        </div>

        {targetStatus === 'in_production' && (
          <div className="flex-shrink-0 px-4 pb-2 sm:px-6">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={useActualManufactureDateInEmail}
                  onChange={(e) => setUseActualManufactureDateInEmail(e.target.checked)}
                  disabled={isLoading}
                  className="h-4 w-4 shrink-0 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Use actual manufacture date in customer email
                </span>
              </label>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-shrink-0 flex-col gap-3 px-4 pb-4 sm:mt-4 sm:flex sm:flex-row-reverse sm:px-6 sm:pb-6">
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className="inline-flex w-full justify-center bg-orange-600 text-white hover:bg-orange-700 sm:ml-3 sm:w-auto"
          >
            {isLoading ? 'Updating...' : `Move to ${targetConfig.label}`}
          </Button>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
            className="inline-flex w-full justify-center sm:mt-0 sm:w-auto"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};
